// src/components/DocAudienceSelect.js
import React, { useEffect, useMemo, useState } from "react";

/**
 * DocAudienceSelect
 * - Dedicated Doc Writer audience selector.
 * - Broad enough for the current Doc Writer subject taxonomy without becoming
 *   another subject taxonomy.
 * - Keeps "Individual / Personal" as a first-class choice.
 * - "Other / Not Listed" reveals a required custom audience field.
 * - Emits the final human-readable audience string through onChange.
 *
 * Props:
 *  - value?: string
 *  - onChange?: (audience: string) => void
 *  - disabled?: boolean
 *  - id?: string
 *  - name?: string
 */

const OTHER_VALUE = "__other_not_listed__";
const CUSTOM_AUDIENCE_MAX_LENGTH = 140;

const AUDIENCE_GROUPS = [
  {
    label: "Personal & General",
    options: [
      "Individual / Personal",
      "Family Member / Relative",
      "Customer / Consumer",
      "General Organization",
    ],
  },
  {
    label: "Courts & Legal",
    options: [
      "Court / Judge",
      "Small Claims Court",
      "Court Clerk / Tribunal",
      "Attorney / Law Firm",
      "Opposing Party / Opposing Counsel",
    ],
  },
  {
    label: "Immigration & Federal Agencies",
    options: [
      "USCIS",
      "National Visa Center (NVC) / U.S. Embassy / Consulate",
      "Immigration Court / EOIR",
      "ICE / CBP / DHS",
      "U.S. Department of State",
      "IRS",
      "Social Security Administration (SSA)",
      "U.S. Small Business Administration (SBA)",
      "U.S. Department of Labor",
      "Other Federal Agency / Regulator",
    ],
  },
  {
    label: "State & Local Government",
    options: [
      "State Tax Agency",
      "State Attorney General / Consumer Protection",
      "DMV / Motor Vehicle Agency",
      "State Professional Licensing Board",
      "Unemployment / Workforce Agency",
      "City / County / Municipality",
    ],
  },
  {
    label: "Business & Employment",
    options: [
      "Employer / Human Resources (HR)",
      "Employee",
      "Small Business / Business Owner",
      "Corporation / LLC / Partnership",
      "Vendor / Supplier",
      "Contractor / Service Provider",
    ],
  },
  {
    label: "Financial, Credit & Insurance",
    options: [
      "Bank / Credit Union",
      "Lender / Mortgage Servicer",
      "Creditor / Debt Collector",
      "Insurance Company",
    ],
  },
  {
    label: "Housing & Property",
    options: [
      "Landlord / Property Owner",
      "Tenant",
      "Property Manager / HOA / Housing Authority",
    ],
  },
  {
    label: "Education",
    options: [
      "K-12 School / School District",
      "College / University",
      "International Student Office / DSO",
    ],
  },
  {
    label: "Healthcare",
    options: [
      "Doctor / Healthcare Provider",
      "Hospital / Medical Facility",
      "Health Insurance Plan / Medical Billing",
    ],
  },
  {
    label: "Technology & Essential Services",
    options: [
      "Utility / Telecom / Internet Provider",
      "Digital Platform / Marketplace",
    ],
  },
]

const ALL_PRESET_AUDIENCES = AUDIENCE_GROUPS.flatMap((group) => group.options);

const LEGACY_AUDIENCE_MAP = {
  gov_agency: "Other Federal Agency / Regulator",
  court_legal: "Court / Judge",
  financial_institution: "Bank / Credit Union",
  housing_property: "Property Manager / HOA / Condo Association",
  employer_school: "Employer / Human Resources (HR)",
  healthcare_insurer: "Health Insurance Plan",
  utility_telecom: "Telecom / Internet Service Provider",
  digital_platform: "Digital Platform / Marketplace",
  law_enforcement: "Police / Public Safety Agency",
  general_business: "Business / Customer Service Department",
  individual_person: "Individual / Personal",
};

const normalizeIncomingAudience = (value) => {
  const raw = value === null || value === undefined ? "" : String(value).trim();
  if (!raw) return "";
  return LEGACY_AUDIENCE_MAP[raw] || raw;
};

const DocAudienceSelect = ({
  value,
  onChange,
  disabled = false,
  id = "doc-writer-audience",
  name = "doc-writer-audience",
}) => {
  const initialAudience = normalizeIncomingAudience(value);

  const initialPreset = ALL_PRESET_AUDIENCES.includes(initialAudience)
    ? initialAudience
    : initialAudience
    ? OTHER_VALUE
    : "";

  const [selectedAudience, setSelectedAudience] = useState(initialPreset);
  const [customAudience, setCustomAudience] = useState(
    initialPreset === OTHER_VALUE ? initialAudience : ""
  );

  const selectedIsOther = selectedAudience === OTHER_VALUE;

  const groups = useMemo(() => AUDIENCE_GROUPS, []);

  // Keep the component safe if the parent changes/reset the controlled value.
  // When "Other" is selected and the emitted value is temporarily empty, keep
  // the custom field open so the user can type the required audience.
  useEffect(() => {
    if (value === undefined) return;

    const incoming = normalizeIncomingAudience(value);

    if (!incoming) {
      if (selectedAudience !== OTHER_VALUE) {
        setSelectedAudience("");
        setCustomAudience("");
      }
      return;
    }

    if (ALL_PRESET_AUDIENCES.includes(incoming)) {
      if (selectedAudience !== incoming) setSelectedAudience(incoming);
      if (customAudience) setCustomAudience("");
      return;
    }

    if (selectedAudience !== OTHER_VALUE) setSelectedAudience(OTHER_VALUE);
    if (customAudience !== incoming) setCustomAudience(incoming);
  }, [value, selectedAudience, customAudience]);

  const emitAudience = (next) => {
    onChange?.(String(next || "").trim());
  };

  const handleAudienceChange = (event) => {
    const next = event.target.value;
    setSelectedAudience(next);

    if (next === OTHER_VALUE) {
      setCustomAudience("");
      // Keep the owning Start Page's required Audience state empty until the
      // user actually specifies the unlisted audience.
      emitAudience("");
      return;
    }

    setCustomAudience("");
    emitAudience(next);
  };

  const handleCustomAudienceChange = (event) => {
    const next = String(event.target.value || "").slice(
      0,
      CUSTOM_AUDIENCE_MAX_LENGTH
    );
    setCustomAudience(next);
    emitAudience(next);
  };

  const handleCustomAudienceBlur = () => {
    const trimmed = customAudience.trim();
    if (trimmed !== customAudience) setCustomAudience(trimmed);
    emitAudience(trimmed);
  };

  const styles = {
    wrap: {
      width: "100%",
      minWidth: 0,
    },
    label: {
      display: "block",
      fontWeight: 600,
      marginBottom: 6,
      fontSize: 13,
      color: "#111827",
    },
    required: {
      color: "#dc2626",
      marginLeft: 2,
    },
    select: {
      width: "100%",
      maxWidth: "100%",
      boxSizing: "border-box",
      padding: "10px 12px",
      border: "1px solid #d1d5db",
      borderRadius: 8,
      fontSize: 14,
      background: "#fff",
      color: "#111827",
    },
    optgroup: {
      fontWeight: 700,
      background: "#f9fafb",
      color: "#111827",
    },
    customWrap: {
      marginTop: 10,
      padding: 12,
      border: "1px solid #e5e7eb",
      borderRadius: 10,
      background: "#fafafa",
    },
    customLabel: {
      display: "block",
      fontWeight: 600,
      marginBottom: 6,
      fontSize: 13,
      color: "#111827",
    },
    customInput: {
      width: "100%",
      maxWidth: "100%",
      boxSizing: "border-box",
      padding: "10px 12px",
      border: "1px solid #d1d5db",
      borderRadius: 8,
      fontSize: 14,
      background: "#fff",
      color: "#111827",
    },
    customMeta: {
      display: "flex",
      justifyContent: "space-between",
      gap: 10,
      marginTop: 6,
      fontSize: 11,
      color: "#6b7280",
    },
    help: {
      fontSize: 12,
      color: "#6b7280",
      marginTop: 6,
      lineHeight: 1.45,
    },
  };

  return (
    <div style={styles.wrap}>
      <label htmlFor={id} style={styles.label}>
        Audience
        <span style={styles.required}>*</span>
      </label>

      <select
        id={id}
        name={name}
        value={selectedAudience}
        onChange={handleAudienceChange}
        style={styles.select}
        aria-label="Audience select"
        aria-describedby={`${id}-help`}
        disabled={disabled}
        required
      >
        <option value="" disabled>
          -- Choose an audience --
        </option>

        {groups.map((group) => (
          <optgroup
            key={group.label}
            label={group.label}
            style={styles.optgroup}
          >
            {group.options.map((audienceOption) => (
              <option key={audienceOption} value={audienceOption}>
                {audienceOption}
              </option>
            ))}
          </optgroup>
        ))}

        <optgroup label="Other" style={styles.optgroup}>
          <option value={OTHER_VALUE}>Other / Not Listed</option>
        </optgroup>
      </select>

      {selectedIsOther ? (
        <div style={styles.customWrap}>
          <label htmlFor={`${id}-custom`} style={styles.customLabel}>
            Specify audience
            <span style={styles.required}>*</span>
          </label>
          <input
            id={`${id}-custom`}
            type="text"
            value={customAudience}
            onChange={handleCustomAudienceChange}
            onBlur={handleCustomAudienceBlur}
            maxLength={CUSTOM_AUDIENCE_MAX_LENGTH}
            placeholder="e.g., County Assessor, University Dean, Embassy, Appeals Officer…"
            style={styles.customInput}
            aria-label="Specify audience"
            disabled={disabled}
            required
            autoComplete="off"
            autoCapitalize="words"
            spellCheck
          />
          <div style={styles.customMeta}>
            <span>Enter the person, office, agency, court, or organization.</span>
            <span>
              {customAudience.length} / {CUSTOM_AUDIENCE_MAX_LENGTH}
            </span>
          </div>
        </div>
      ) : null}

      <div id={`${id}-help`} style={styles.help}>
        Choose the person, court, agency, business, institution, or organization
        expected to receive or read the document.
      </div>
    </div>
  );
};

export default DocAudienceSelect;
